import { useState } from 'react';
import { Link } from 'react-router-dom';
import { localDeals } from '../../../mocks/deals';

interface FeaturedDealsProps {
  searchQuery: string;
}

export default function FeaturedDeals({ searchQuery }: FeaturedDealsProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const featured = localDeals.filter((d) => d.featured);
  const filtered = searchQuery
    ? featured.filter(
        (d) =>
          d.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.dealTitle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : featured;

  if (filtered.length === 0) return null;

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy coupon code:', err);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-b from-amber-50/50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-fire-line text-amber-500 text-xl"></i>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">
                Hot Deals
              </span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Deals This Week</h2>
          </div>
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <i className="ri-time-line"></i>
            Limited time offers
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {filtered.map((deal) => (
            <Link
              to={`/deals/${deal.id}`}
              key={deal.id}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer block"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={deal.image}
                  alt={`${deal.dealTitle} at ${deal.businessName} Roseville CA`}
                  title={`${deal.businessName} deal Roseville`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                    <i className="ri-fire-fill text-xs"></i>
                    {deal.discountPercent === 100 ? 'FREE' : `${deal.discountPercent}% OFF`}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-semibold rounded-full">
                    <i className="ri-time-line mr-1"></i>
                    {deal.expiresAt}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${deal.businessLogo} text-teal-600 text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 truncate">{deal.businessName}</p>
                    <h3 className="text-base font-bold text-gray-900 truncate">{deal.dealTitle}</h3>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{deal.description}</p>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-baseline gap-2">
                    {deal.dealPrice > 0 ? (
                      <>
                        <span className="text-xl font-bold text-teal-700">${deal.dealPrice}</span>
                        <span className="text-sm text-gray-400 line-through">
                          ${deal.originalPrice}
                        </span>
                      </>
                    ) : (
                      <span className="text-xl font-bold text-teal-700">FREE</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                    <span className="text-sm font-semibold text-gray-800">{deal.rating}</span>
                    <span className="text-xs text-gray-400">({deal.reviews})</span>
                  </div>
                </div>

                <div className="flex items-center gap-2" onClick={(e) => e.preventDefault()}>
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <i className="ri-scissors-cut-line text-teal-600 text-sm"></i>
                    <span className="text-sm font-mono font-bold text-gray-800 tracking-wide">
                      {deal.couponCode}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCopy(deal.couponCode);
                    }}
                    className="whitespace-nowrap px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                  >
                    {copiedCode === deal.couponCode ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-check-line"></i> Copied
                      </span>
                    ) : (
                      'Copy'
                    )}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                  <i className="ri-group-line"></i>
                  {deal.claimed} people claimed this deal
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
